// Tela do guincheiro (pedido 2026-10-16) — aberta pelo link da mensagem de
// WhatsApp, SEM login (o token na URL é a credencial; bypass em __root.tsx).
// Mostra o Ponto A (veículo, ao vivo pelo rastreador) e o Ponto B (destino),
// e o botão "Iniciar trajeto": a partir daí o GPS do celular é enviado ao
// servidor e o cliente acompanha em /acompanhar/:token.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CheckCircle2, Flag, Loader2, MapPin, MessageCircle, Navigation, Phone, TriangleAlert, Truck } from "lucide-react";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import { gpsDriveAssistancePublicService as api, type PublicTowView, type TowRoute } from "@/services/gpsdrive-assistance-center.service";
import { AssistanceMap, fmtDistance, fmtDuration, metersBetween, type LatLng } from "@/components/assistance-public-map";

export const Route = createFileRoute("/guincho/$token")({
  component: TowDriverPage,
  head: () => ({ meta: [{ title: "Atendimento de guincho" }, { name: "robots", content: "noindex" }] }),
});

const POLL_MS = 10_000;
const SEND_MS = 4_000;

function useWakeLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    let lock: { release: () => Promise<void> } | null = null;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } };
    const get = () => nav.wakeLock?.request("screen").then((l) => (lock = l)).catch(() => undefined);
    void get();
    const onVis = () => !document.hidden && void get();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      void lock?.release().catch(() => undefined);
    };
  }, [enabled]);
}

function TowDriverPage() {
  const { token } = Route.useParams();
  const [data, setData] = useState<PublicTowView | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [netError, setNetError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [me, setMe] = useState<(LatLng & { speedKmh: number | null; heading: number | null; accuracy: number }) | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [route, setRoute] = useState<TowRoute | null>(null);
  const lastSent = useRef(0);
  const lastRouteFrom = useRef<LatLng | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.tow(token));
      setNetError(false);
    } catch (e) {
      if (e instanceof GpsDriveApiError && e.status === 404) setNotFound(true);
      else setNetError(true);
    }
  }, [token]);

  useEffect(() => {
    void load();
    const t = setInterval(() => !document.hidden && void load(), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const phase = data?.phase;
  const tracking = phase === "a_caminho" || phase === "no_local" || phase === "levando";
  useWakeLock(tracking);

  // GPS do celular: sempre que a tela está aberta (mostra "você" no mapa e
  // calcula a rota); só ENVIA ao servidor depois de Iniciar trajeto.
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGpsError("Este aparelho não tem GPS disponível no navegador.");
      return;
    }
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setGpsError(null);
        setMe({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracy: p.coords.accuracy,
          speedKmh: p.coords.speed != null ? Math.round(p.coords.speed * 3.6) : null,
          heading: p.coords.heading != null && !Number.isNaN(p.coords.heading) ? p.coords.heading : null,
        });
      },
      (err) => setGpsError(err.code === err.PERMISSION_DENIED ? "Permita a localização para o cliente acompanhar o trajeto." : "Sem sinal de GPS no momento."),
      { enableHighAccuracy: true, maximumAge: 3_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  useEffect(() => {
    if (!tracking || !me) return;
    if (Date.now() - lastSent.current < SEND_MS) return;
    lastSent.current = Date.now();
    void api.towPosition(token, { lat: me.lat, lng: me.lng, speedKmh: me.speedKmh, heading: me.heading }).catch(() => undefined);
  }, [tracking, me, token]);

  // Rota até o alvo da etapa (veículo; depois de "Sair para o destino", o B).
  const target: "a" | "b" = phase === "levando" || phase === "no_local" ? "b" : "a";
  useEffect(() => {
    if (!me || !data) return;
    if (target === "b" && (data.pointB?.lat == null || data.pointB.lng == null)) return setRoute(null);
    if (target === "a" && !data.pointA) return setRoute(null);
    const moved = !lastRouteFrom.current || metersBetween(lastRouteFrom.current, me) > 120;
    if (!moved && route) return;
    lastRouteFrom.current = { lat: me.lat, lng: me.lng };
    api
      .towRoute(token, me, target)
      .then(setRoute)
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.lat, me?.lng, target, data?.pointA?.lat, data?.pointA?.lng]);
  useEffect(() => {
    lastRouteFrom.current = null;
    setRoute(null);
  }, [target]);

  const act = async (action: "start" | "arrived" | "depart" | "finish", confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setActionError(null);
    try {
      setData(await api.towAction(token, action));
      if (action === "start" && me) {
        lastSent.current = Date.now();
        void api.towPosition(token, { lat: me.lat, lng: me.lng, speedKmh: me.speedKmh, heading: me.heading }).catch(() => undefined);
      }
    } catch (e) {
      setActionError(e instanceof GpsDriveApiError ? e.message : "Sem conexão — tente de novo.");
      void load();
    } finally {
      setBusy(false);
    }
  };

  const a = data?.pointA ? { lat: data.pointA.lat, lng: data.pointA.lng } : null;
  const b = data?.pointB?.lat != null && data.pointB.lng != null ? { lat: data.pointB.lat, lng: data.pointB.lng } : null;
  const targetPoint = target === "b" ? b : a;
  const path = useMemo(() => route?.geometry.map(([lat, lng]) => ({ lat, lng })) ?? null, [route]);
  const navLinks = targetPoint
    ? {
        waze: `https://waze.com/ul?ll=${targetPoint.lat},${targetPoint.lng}&navigate=yes`,
        google: `https://www.google.com/maps/dir/?api=1&destination=${targetPoint.lat},${targetPoint.lng}&travelmode=driving`,
      }
    : null;

  if (notFound) return <Full icon={<TriangleAlert className="h-10 w-10 text-amber-400" />} title="Link inválido" text="Este link não existe. Peça um novo à central." />;
  if (!data)
    return (
      <Full
        icon={netError ? <TriangleAlert className="h-10 w-10 text-amber-400" /> : <Loader2 className="h-10 w-10 animate-spin text-blue-400" />}
        title={netError ? "Sem conexão" : "Carregando atendimento…"}
        text={netError ? "Verifique a internet. Tentando de novo…" : ""}
      />
    );
  if (data.phase === "cancelado") return <Full icon={<TriangleAlert className="h-10 w-10 text-slate-300" />} title="Atendimento cancelado" text="A central cancelou este envio. Obrigado!" />;
  if (data.takenByOther && !data.startedAt)
    return <Full icon={<Truck className="h-10 w-10 text-slate-300" />} title="Atendimento já assumido" text={`Outro guincho (${data.takenByOther}) já está a caminho. Obrigado pela disponibilidade!`} />;

  const clientWa = data.clientPhone ? `https://wa.me/${waNumber(data.clientPhone)}` : null;

  return (
    <div className="flex h-[100dvh] flex-col bg-slate-950 text-white">
      <header className="flex items-center gap-3 border-b border-white/10 px-4 pb-3" style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}>
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-blue-600 text-xl">🛻</div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold">{data.service}</div>
          <div className="truncate text-xs text-slate-400">{data.towTruckName}</div>
        </div>
        <PhaseBadge phase={data.phase} />
      </header>

      <div className="relative min-h-0 flex-1">
        <AssistanceMap a={a} b={b} me={me} route={path} className="h-full w-full" />
        {route && (
          <div className="absolute left-3 top-3 rounded-xl bg-slate-900/90 px-3 py-2 text-xs shadow-lg backdrop-blur">
            <div className="text-slate-400">Até o {target === "b" ? "destino (B)" : "veículo (A)"}</div>
            <div className="text-lg font-bold">
              {fmtDuration(route.durationS)} <span className="text-sm font-medium text-slate-300">· {fmtDistance(route.distanceM)}</span>
            </div>
          </div>
        )}
        {gpsError && <div className="absolute inset-x-3 bottom-3 rounded-xl bg-amber-500 px-3 py-2 text-xs font-semibold text-slate-950 shadow-lg">{gpsError}</div>}
      </div>

      <div className="max-h-[52dvh] space-y-3 overflow-y-auto rounded-t-2xl bg-slate-900 px-4 pt-4" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
        {/* Ação principal da etapa */}
        {data.phase === "concluido" ? (
          <div className="flex items-center gap-2 rounded-xl bg-emerald-600/20 p-3 text-sm font-semibold text-emerald-300">
            <CheckCircle2 className="h-5 w-5" /> Atendimento finalizado. Obrigado!
          </div>
        ) : (
          <>
            {data.phase === "aguardando" && (
              <BigButton onClick={() => act("start")} busy={busy} className="bg-emerald-600">
                <Navigation className="h-5 w-5" /> Iniciar trajeto
              </BigButton>
            )}
            {data.phase === "a_caminho" && (
              <BigButton onClick={() => act("arrived")} busy={busy} className="bg-amber-500 text-slate-950">
                <MapPin className="h-5 w-5" /> Cheguei no veículo
              </BigButton>
            )}
            {data.phase === "no_local" && (
              <BigButton onClick={() => act("depart")} busy={busy} className="bg-violet-600">
                <Truck className="h-5 w-5" /> Sair para o destino (B)
              </BigButton>
            )}
            {data.phase === "levando" && (
              <BigButton onClick={() => act("finish", "Confirmar que o veículo foi entregue no destino?")} busy={busy} className="bg-emerald-600">
                <Flag className="h-5 w-5" /> Finalizar atendimento
              </BigButton>
            )}
            {tracking && <p className="text-center text-[11px] text-slate-400">Mantenha esta tela aberta: o cliente acompanha sua localização em tempo real.</p>}
            {actionError && <p className="rounded-lg bg-red-500/20 px-3 py-2 text-sm text-red-300">{actionError}</p>}
            {navLinks && (
              <div className="grid grid-cols-2 gap-2">
                <a href={navLinks.waze} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-white/10 text-sm font-semibold">
                  Abrir no Waze
                </a>
                <a href={navLinks.google} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-white/10 text-sm font-semibold">
                  Google Maps
                </a>
              </div>
            )}
          </>
        )}

        <Info label="Cliente">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-semibold">{data.clientName}</span>
            {data.clientPhone && (
              <>
                <a href={`tel:${data.clientPhone.replace(/\D/g, "")}`} className="grid h-9 w-9 place-items-center rounded-full bg-white/10" aria-label="Ligar para o cliente">
                  <Phone className="h-4 w-4" />
                </a>
                {clientWa && (
                  <a href={clientWa} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center rounded-full bg-emerald-600" aria-label="WhatsApp do cliente">
                    <MessageCircle className="h-4 w-4" />
                  </a>
                )}
              </>
            )}
          </div>
        </Info>
        <Info label="Veículo">
          <div className="font-semibold">{data.vehicle.model ?? data.vehicle.label}</div>
          <div className="text-xs text-slate-400">
            {[data.vehicle.color, data.vehicle.plate].filter(Boolean).join(" · ") || data.vehicle.label}
          </div>
        </Info>
        <Info label="Ponto A — onde o veículo está">
          <div className="text-sm">{data.pointA?.address ?? (a ? "Posição do rastreador no mapa" : "Sem posição do rastreador")}</div>
          {data.pointA?.fixTime && <div className="text-[11px] text-slate-500">Atualizado {new Date(data.pointA.fixTime).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</div>}
        </Info>
        <Info label="Ponto B — destino">
          <div className="text-sm">{data.pointB?.address ?? "Não informado"}</div>
        </Info>
        {data.reason && (
          <Info label="Observação">
            <div className="text-sm">{data.reason}</div>
          </Info>
        )}
      </div>
    </div>
  );
}

function waNumber(phone: string) {
  const d = phone.replace(/\D/g, "");
  return d.length <= 11 ? `55${d}` : d;
}

function PhaseBadge({ phase }: { phase: PublicTowView["phase"] }) {
  const map: Record<string, [string, string]> = {
    aguardando: ["Aguardando", "bg-slate-700"],
    a_caminho: ["A caminho", "bg-blue-600"],
    no_local: ["No local", "bg-amber-500 text-slate-950"],
    levando: ["Levando", "bg-violet-600"],
    concluido: ["Concluído", "bg-emerald-600"],
    cancelado: ["Cancelado", "bg-slate-700"],
  };
  const [label, cls] = map[phase] ?? ["", ""];
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${cls}`}>{label}</span>;
}

function BigButton({ children, onClick, busy, className }: { children: ReactNode; onClick: () => void; busy: boolean; className: string }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} className={`flex h-14 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold shadow-lg active:scale-[0.99] disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : children}
    </button>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-xl bg-white/5 px-3 py-2.5">
      <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      {children}
    </div>
  );
}

function Full({ icon, title, text }: { icon: ReactNode; title: string; text: string }) {
  return (
    <div className="flex h-[100dvh] flex-col items-center justify-center gap-3 bg-slate-950 p-8 text-center text-white">
      {icon}
      <h1 className="text-xl font-bold">{title}</h1>
      {text && <p className="max-w-xs text-sm text-slate-400">{text}</p>}
    </div>
  );
}
